import Link from 'next/link'
import { HowStringsSGWorksPage } from '@/index/HowStringsSGWorks'

/**
 * Handyman trust page. Shared structure and voice: components/index/HowStringsSGWorks.js.
 *
 * Handyman-specific sources - keep claims to these:
 *  - per-job scope (what it covers, materials, exclusions): the price list
 *    items, shown in the booking flow (HandymanJobDetailsStep)
 *  - fixed published prices; supply-and-install includes the part, other parts
 *    agreed first: utils/faq.js handymanSingaporeFaqs
 *  - warranty: /terms/warranty-claims - 30 days workmanship, rectified at no
 *    additional cost; installed items under the manufacturer's warranty
 *  - 100% satisfaction guarantee (StringsSG): "we'll make it right". No
 *    money-back promise - StringsSG does not offer one.
 */

// When this page's content last actually changed. Bump it by hand when you edit the copy.
const CONTENT_REVIEWED = '2026-09-27'

const link = (href, label) => (
    <Link href={href} prefetch={false} className="text-orange-600 hover:underline">{label}</Link>
)

export default function HowStringsSGWorksHandyman() {
    return (
        <HowStringsSGWorksPage
            path="/sg/handyman-singapore-services/how-stringssg-works"
            title="How StringsSG Handyman Services Work | Our Service Standards"
            h1="How StringsSG Handyman Services Work"
            description="StringsSG defines the scope and quality standard of every handyman job and takes responsibility for the result. How we vet handymen, our fixed prices, our 30-day warranty and 100% satisfaction guarantee."
            reviewed={CONTENT_REVIEWED}
            intro="StringsSG provides handyman services across Singapore - plumbing and electrical work, door locks and handles, TV and wall mounting, curtain rails, furniture assembly and painting. We set how every job is done and take responsibility for the result."
            serviceNoun="handyman job"
            people="handymen"
            vetting="we vet them and check the licences of the ACRA-registered businesses they work for"
            standards={
                <p>
                    Every handyman job on our price list has a defined scope: what the job covers, whether materials
                    are included, and what is excluded. You see it before you confirm, and the job is done to that
                    scope whoever carries it out.{' '}
                    {link('/sg/handyman-singapore-services/handyman-price-list', 'Handyman price list')}
                </p>
            }
            pricing={
                <p>
                    Every handyman job has a fixed, published price that you see before confirming the booking. Jobs
                    listed as supply and install include the part; if any other parts are needed, their cost is agreed
                    with you before work starts.
                </p>
            }
            afterService={
                <p>
                    Every handyman job is backed by our 100% satisfaction guarantee: if you are not happy with the work,
                    contact us through the app or on WhatsApp and we will make it right. The work also carries a 30-day
                    workmanship warranty - any issue related to or caused by the job within 30 days is rectified at no
                    additional cost - and installed items carry the manufacturer&apos;s warranty.{' '}
                    {link('/terms/warranty-claims', 'Warranty terms')}
                </p>
            }
            bookHref="/sg/handyman-singapore-services"
            bookLabel="Book a handyman"
        />
    )
}
